import React, { useState, useMemo, useRef } from "react";
import { SVG_ICONS } from "./TablerIconPicker";
import "../styles/PublicationCalendar.css";

const PublicationCalendar = ({ allItems, onEditItem, onViewItem, onRescheduleItem, showNextMonth = true }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dropTargetDate, setDropTargetDate] = useState(null);
  const calendarRef = useRef(null);
  const scrollTimerRef = useRef(null);

  const parseLocalDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-');
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  };

  const daysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();

  const itemsByDate = useMemo(() => {
    const map = {};

    allItems?.forEach((item) => {
      // Check scheduled date (supports both scheduled_date and scheduled fields)
      const scheduledDate = item.scheduled_date || item.scheduled;
      if (scheduledDate) {
        const date = new Date(scheduledDate).toLocaleDateString("en-CA"); // YYYY-MM-DD format
        if (!map[date]) map[date] = { scheduled: [], published: [] };
        map[date].scheduled.push(item);
      }

      // Check published date (for draft status)
      // For published items, use publishedDate if available, otherwise addedAt
      if (!item.draft) {
        const displayDate = item.publishedDate || item.addedAt;
        if (displayDate) {
          const date = new Date(displayDate).toLocaleDateString("en-CA");
          if (!map[date]) map[date] = { scheduled: [], published: [] };
          map[date].published.push(item);
        }
      }

      // For news, check published_at
      if (item.contentType === "News" && item.published_at && !item.archived) {
        const date = new Date(item.published_at).toLocaleDateString("en-CA");
        if (!map[date]) map[date] = { scheduled: [], published: [] };
        map[date].published.push(item);
      }
    });

    return map;
  }, [allItems]);

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleDragStart = (e, item) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/json", JSON.stringify(item));
  };

  const handleDragOver = (e, dateStr) => {
    const dropDate = parseLocalDate(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Only allow drop on future dates
    if (dropDate > today) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setDropTargetDate(dateStr);

      // Auto-scroll and navigate when dragging near edges
      if (calendarRef.current) {
        const rect = calendarRef.current.getBoundingClientRect();
        const scrollThreshold = 80;
        const autoScrollSpeed = 30;

        // Near top - scroll up and go to previous month
        if (e.clientY - rect.top < scrollThreshold) {
          calendarRef.current.scrollBy(0, -autoScrollSpeed);
          if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
          scrollTimerRef.current = setTimeout(() => {
            handlePrevMonth();
          }, 500);
        }
        // Near bottom - scroll down and go to next month
        else if (rect.bottom - e.clientY < scrollThreshold) {
          calendarRef.current.scrollBy(0, autoScrollSpeed);
          if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
          scrollTimerRef.current = setTimeout(() => {
            handleNextMonth();
          }, 500);
        } else {
          if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
        }
      }
    }
  };

  const handleDragLeave = () => {
    setDropTargetDate(null);
    if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
  };

  const handleDrop = (e, dateStr) => {
    e.preventDefault();

    let itemToDrop = draggedItem;

    // If no draggedItem from state, try to extract from dataTransfer (sidebar items)
    if (!itemToDrop) {
      try {
        const data = e.dataTransfer.getData("application/json");
        if (data) {
          itemToDrop = JSON.parse(data);
        }
      } catch (err) {
        console.error("Error parsing drop data:", err);
      }
    }

    if (!itemToDrop) return;

    const dropDate = parseLocalDate(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Only allow drop on future dates
    if (dropDate > today && onRescheduleItem) {
      onRescheduleItem(itemToDrop, dateStr);
    }
    setDraggedItem(null);
    setDropTargetDate(null);
    if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
  };

  const monthName = currentDate.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const days = Array.from({ length: daysInMonth(currentDate) }, (_, i) => i + 1);
  const emptyDays = firstDayOfMonth(currentDate);

  return (
    <div className="publication-calendar" ref={calendarRef}>
      <div className="calendar-header">
        <button onClick={handlePrevMonth} className="nav-btn">
          {SVG_ICONS["chevron-left"]({ size: 16, color: "#5B9EF0" })}
        </button>
        <h2 className="month-title">{monthName}</h2>
        <button onClick={handleNextMonth} className="nav-btn">
          {SVG_ICONS["chevron-right"]({ size: 16, color: "#5B9EF0" })}
        </button>
        <button onClick={handleToday} className="today-btn">Today</button>
      </div>

      <div className="calendars-container" style={{ gridTemplateColumns: showNextMonth ? "1fr 1fr" : "1fr" }}>
        {/* Current Month */}
        <div className="single-month">
          <div className="weekdays">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
              <div key={day} className="weekday">
                {day}
              </div>
            ))}
          </div>

          <div className="calendar-grid">
        {/* Empty cells for days before month starts */}
        {Array.from({ length: emptyDays }).map((_, i) => (
          <div key={`empty-${i}`} className="calendar-day empty"></div>
        ))}

        {/* Days of the month */}
        {days.map((day) => {
          const dateStr = new Date(currentDate.getFullYear(), currentDate.getMonth(), day)
            .toLocaleDateString("en-CA");
          const dayItems = itemsByDate[dateStr];
          const isToday =
            new Date().toLocaleDateString("en-CA") === dateStr;
          const isSelected = selectedDate === dateStr;

          return (
            <div
              key={day}
              onClick={() => setSelectedDate(dateStr)}
              onDragOver={(e) => handleDragOver(e, dateStr)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, dateStr)}
              className={`calendar-day ${isToday ? "today" : ""} ${dayItems ? "has-items" : ""} ${isSelected ? "selected" : ""} ${dropTargetDate === dateStr ? "drop-target-active" : ""}`}
              style={{ cursor: "pointer" }}
            >
              <div className="day-number">{day}</div>
              <div className="day-items">
                {dayItems?.scheduled?.map((item, idx) => {
                  const typeColors = {
                    "Lesson": { bg: "rgba(52, 211, 153, 0.7)", color: "#ffffff" },
                    "Prompt": { bg: "rgba(168, 85, 247, 0.7)", color: "#ffffff" },
                    "Resource": { bg: "rgba(245, 158, 11, 0.7)", color: "#ffffff" },
                    "News": { bg: "rgba(239, 68, 68, 0.7)", color: "#ffffff" }
                  };
                  const typeStyle = typeColors[item.contentType] || { bg: "rgba(91, 158, 240, 0.7)", color: "#ffffff" };
                  return (
                    <div
                      key={`sched-${idx}`}
                      className="item scheduled"
                      title={item.title || item.name}
                      draggable
                      onDragStart={(e) => handleDragStart(e, item)}
                      onDragEnd={() => setDraggedItem(null)}
                      style={{ cursor: "grab", opacity: draggedItem?.id === item.id ? 0.5 : 1, background: typeStyle.bg, color: typeStyle.color }}
                    >
                      <span className="type-badge">{item.contentType?.[0]}</span>
                      <span className="status-dot pending"></span>
                    </div>
                  );
                })}
                {dayItems?.published?.map((item, idx) => {
                  const typeColors = {
                    "Lesson": { bg: "rgba(52, 211, 153, 0.7)", color: "#ffffff" },
                    "Prompt": { bg: "rgba(168, 85, 247, 0.7)", color: "#ffffff" },
                    "Resource": { bg: "rgba(245, 158, 11, 0.7)", color: "#ffffff" },
                    "News": { bg: "rgba(239, 68, 68, 0.7)", color: "#ffffff" }
                  };
                  const typeStyle = typeColors[item.contentType] || { bg: "rgba(91, 158, 240, 0.7)", color: "#ffffff" };
                  return (
                    <div key={`pub-${idx}`} className="item published" title={item.title || item.name} style={{ background: typeStyle.bg, color: typeStyle.color }}>
                      <span className="type-badge">{item.contentType?.[0]}</span>
                      <span className="status-check">✓</span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
          </div>
        </div>

        {/* Next Month */}
        {showNextMonth && (() => {
          const nextDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1);
          const nextDays = Array.from({ length: daysInMonth(nextDate) }, (_, i) => i + 1);
          const nextEmptyDays = firstDayOfMonth(nextDate);
          const nextMonthName = nextDate.toLocaleDateString("en-US", { month: "long", year: "numeric" });

          return (
            <div className="single-month">
              <div className="month-label">{nextMonthName}</div>
              <div className="weekdays">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                  <div key={day} className="weekday">
                    {day}
                  </div>
                ))}
              </div>

              <div className="calendar-grid">
                {Array.from({ length: nextEmptyDays }).map((_, i) => (
                  <div key={`empty-${i}`} className="calendar-day empty"></div>
                ))}

                {nextDays.map((day) => {
                  const dateStr = new Date(nextDate.getFullYear(), nextDate.getMonth(), day)
                    .toLocaleDateString("en-CA");
                  const dayItems = itemsByDate[dateStr];
                  const isToday =
                    new Date().toLocaleDateString("en-CA") === dateStr;
                  const isSelected = selectedDate === dateStr;

                  return (
                    <div
                      key={day}
                      onClick={() => setSelectedDate(dateStr)}
                      onDragOver={(e) => handleDragOver(e, dateStr)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, dateStr)}
                      className={`calendar-day ${isToday ? "today" : ""} ${dayItems ? "has-items" : ""} ${isSelected ? "selected" : ""} ${dropTargetDate === dateStr ? "drop-target-active" : ""}`}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="day-number">{day}</div>
                      <div className="day-items">
                        {dayItems?.scheduled?.map((item, idx) => {
                          const typeColors = {
                            "Lesson": { bg: "rgba(52, 211, 153, 0.7)", color: "#ffffff" },
                            "Prompt": { bg: "rgba(168, 85, 247, 0.7)", color: "#ffffff" },
                            "Resource": { bg: "rgba(245, 158, 11, 0.7)", color: "#ffffff" },
                            "News": { bg: "rgba(239, 68, 68, 0.7)", color: "#ffffff" }
                          };
                          const typeStyle = typeColors[item.contentType] || { bg: "rgba(91, 158, 240, 0.7)", color: "#ffffff" };
                          return (
                            <div
                              key={`sched-${idx}`}
                              className="item scheduled"
                              title={item.title || item.name}
                              draggable
                              onDragStart={(e) => handleDragStart(e, item)}
                              onDragEnd={() => setDraggedItem(null)}
                              style={{ cursor: "grab", opacity: draggedItem?.id === item.id ? 0.5 : 1, background: typeStyle.bg, color: typeStyle.color }}
                            >
                              <span className="type-badge">{item.contentType?.[0]}</span>
                              <span className="status-dot pending"></span>
                            </div>
                          );
                        })}
                        {dayItems?.published?.map((item, idx) => {
                          const typeColors = {
                            "Lesson": { bg: "rgba(52, 211, 153, 0.7)", color: "#ffffff" },
                            "Prompt": { bg: "rgba(168, 85, 247, 0.7)", color: "#ffffff" },
                            "Resource": { bg: "rgba(245, 158, 11, 0.7)", color: "#ffffff" },
                            "News": { bg: "rgba(239, 68, 68, 0.7)", color: "#ffffff" }
                          };
                          const typeStyle = typeColors[item.contentType] || { bg: "rgba(91, 158, 240, 0.7)", color: "#ffffff" };
                          return (
                            <div key={`pub-${idx}`} className="item published" title={item.title || item.name} style={{ background: typeStyle.bg, color: typeStyle.color }}>
                              <span className="type-badge">{item.contentType?.[0]}</span>
                              <span className="status-dot published"></span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </div>

      <div className="calendar-legend">
        <div className="legend-row">
          <div className="legend-item">
            <div className="legend-color" style={{ background: "rgba(52, 211, 153, 0.7)" }}></div>
            <span>Lesson</span>
          </div>
          <div className="legend-item">
            <div className="legend-color" style={{ background: "rgba(168, 85, 247, 0.7)" }}></div>
            <span>Prompt</span>
          </div>
          <div className="legend-item">
            <div className="legend-color" style={{ background: "rgba(245, 158, 11, 0.7)" }}></div>
            <span>Resource</span>
          </div>
          <div className="legend-item">
            <div className="legend-color" style={{ background: "rgba(239, 68, 68, 0.7)" }}></div>
            <span>News</span>
          </div>
        </div>
        <div className="legend-row">
          <div className="legend-item">
            <div className="legend-dot pending"></div>
            <span>Pending</span>
          </div>
          <div className="legend-item">
            <span className="legend-check">✓</span>
            <span>Published</span>
          </div>
        </div>
      </div>

      {selectedDate && (
        <div className="selected-day-details">
          <h3>
            {new Date(selectedDate + "T00:00:00").toLocaleDateString("en-US", {
              weekday: "long",
              month: "long",
              day: "numeric",
              year: "numeric"
            })}
          </h3>

          {itemsByDate[selectedDate]?.scheduled?.length > 0 && (
            <div className="items-section">
              <h4>Scheduled</h4>
              <div className="items-list">
                {itemsByDate[selectedDate].scheduled.map((item, idx) => (
                  <div key={`sched-detail-${idx}`} className="item-detail">
                    <div className="item-info">
                      <span className={`item-type item-type--${item.contentType.toLowerCase()}`}>{item.contentType}</span>
                      <span className="item-title">{item.title || item.name}</span>
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        onClick={() => onViewItem && onViewItem(item)}
                        className="action-link view-link"
                      >
                        View
                      </button>
                      <button
                        onClick={() => onEditItem && onEditItem(item)}
                        className="action-link edit-link"
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {itemsByDate[selectedDate]?.published?.length > 0 && (
            <div className="items-section">
              <h4>Published</h4>
              <div className="items-list">
                {itemsByDate[selectedDate].published.map((item, idx) => (
                  <div key={`pub-detail-${idx}`} className="item-detail">
                    <div className="item-info">
                      <span className={`item-type item-type--${item.contentType.toLowerCase()}`}>{item.contentType}</span>
                      <span className="item-title">{item.title || item.name}</span>
                    </div>
                    <button
                      onClick={() => onViewItem && onViewItem(item)}
                      className="action-link view-link"
                    >
                      View
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!itemsByDate[selectedDate] && (
            <p className="no-items">No items scheduled or published for this day</p>
          )}
        </div>
      )}

    </div>
  );
};

export default PublicationCalendar;
